import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { PlatformSettingsProvider } from './contexts/PlatformSettingsContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import ProtectedRoute from './components/ProtectedRoute';
import SupportModeBanner from './components/SupportModeBanner';

// Pages
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import LoginPage from './pages/LoginPage';
import LogoutPage from './pages/LogoutPage';
import SupportDecidePage from './pages/SupportDecidePage';
import WorkerDashboard from './pages/WorkerDashboard';
import EmployerDashboard from './pages/EmployerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ProgramManagerLogin from './pages/ProgramManagerLogin';
import PMDashboardPage from './pages/PMDashboardPage';

// Legal / public pages — replaces footer placeholders flagged by UAT
import AboutPage from './pages/legal/AboutPage';
import CareersPage from './pages/legal/CareersPage';
import ContactPage from './pages/legal/ContactPage';
import BlogPage from './pages/legal/BlogPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import HIPAAPage from './pages/legal/HIPAAPage';
import CookiesPage from './pages/legal/CookiesPage';
import EnterprisePage from './pages/legal/EnterprisePage';
import SecurityPage from './pages/legal/SecurityPage';
import NotFoundPage from './pages/NotFoundPage';
import VerifyCertificatePage from './pages/VerifyCertificatePage';
import TrainingCertificatePage from './pages/TrainingCertificatePage';
import HrPortalPage from './pages/HrPortalPage';
import SessionSecurityManager from './components/security/SessionSecurityManager';
import TermsGate from './components/security/TermsGate';

function App() {
  return (
    <AuthProvider>
      <PlatformSettingsProvider>
      <WebSocketProvider>
        <Router>
          <div className="App">
            <SupportModeBanner />
            <SessionSecurityManager />
            <TermsGate />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/logout" element={<LogoutPage />} />
              <Route path="/pm-login" element={<ProgramManagerLogin />} />

              {/* Legal / company pages */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/hipaa" element={<HIPAAPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/enterprise" element={<EnterprisePage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/support-decide" element={<SupportDecidePage />} />
              {/* Public certificate verification — no auth needed */}
              <Route path="/verify/:certId" element={<VerifyCertificatePage />} />
              <Route path="/training-certificate/:certId" element={<TrainingCertificatePage />} />
              
              {/* Protected routes */}
              <Route
                path="/worker"
                element={
                  <ProtectedRoute allowedRoles={['worker']}>
                    <WorkerDashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/employer"
                element={
                  <ProtectedRoute allowedRoles={['employer']}>
                    <EmployerDashboard />
                  </ProtectedRoute>
                }
              />
              
              {/* Program Manager Dashboard - separate from employer */}
              <Route
                path="/pm-dashboard"
                element={
                  <ProtectedRoute allowedRoles={['employer']}>
                    <PMDashboardPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/agent"
                element={
                  <ProtectedRoute allowedRoles={['agent']}>
                    <EmployerDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hr"
                element={
                  <ProtectedRoute allowedRoles={['hr']}>
                    <HrPortalPage />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* 404 — must be last */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            
            <Toaster position="top-right" />
          </div>
        </Router>
      </WebSocketProvider>
      </PlatformSettingsProvider>
    </AuthProvider>
  );
}

export default App;
